import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { PRIORITY_COLORS, STATUS_COLORS, ROLES } from '../utils/config';

function CnTextarea({ value, onChange, placeholder, style, rows }) {
  const composing = useRef(false);
  const [local, setLocal] = useState(value || '');
  useEffect(() => { if (!composing.current) setLocal(value || ''); }, [value]);
  return (
    <textarea value={local} placeholder={placeholder} style={style} rows={rows||3}
      onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={e => { composing.current = false; setLocal(e.target.value); onChange(e.target.value); }} />
  );
}

function CnInput({ value, onChange, placeholder, style }) {
  const composing = useRef(false);
  const [local, setLocal] = useState(value || '');
  useEffect(() => { if (!composing.current) setLocal(value || ''); }, [value]);
  return (
    <input value={local} placeholder={placeholder} style={style}
      onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={e => { composing.current = false; setLocal(e.target.value); onChange(e.target.value); }} />
  );
}

export default function TaskDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeImage, setCompleteImage] = useState(null);
  const [completePreview, setCompletePreview] = useState(null);
  const [completeNote, setCompleteNote] = useState('');

  const isManager = ['admin','manager','general_manager'].includes(user.role);
  const isEngineer = ['admin','engineer'].includes(user.role);
  const isCommittee = ['admin','committee'].includes(user.role);
  const isAdmin = user.role === 'admin';

  const fetchTask = async () => {
    try {
      const res = await API.get(`/tasks/${id}`);
      setTask(res.data);
    } catch (err) {
      alert('获取失败');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTask();
    if (isManager) {
      API.get('/users').then(r => setUsers(r.data.filter(u => u.is_active && !['owner','committee'].includes(u.role)))).catch(()=>{});
    }
  }, [id]);

  const handleSave = async (fields) => {
    setSaving(true);
    try {
      await API.put(`/tasks/${id}`, fields);
      await fetchTask();
      setEditing({});
      alert('保存成功');
    } catch (err) {
      alert(err.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!completeImage) { alert('请拍照上传完成照片'); return; }
    setSaving(true);
    try {
      const imgData = new FormData();
      imgData.append('images', completeImage);
      if (completeNote) imgData.append('note', completeNote);
      await API.post(`/tasks/${id}/complete-image`, imgData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchTask();
      setShowCompleteModal(false);
      alert('已标记完成');
    } catch (err) {
      alert(err.response?.data?.message || '提交失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确定删除此事项？删除后不可恢复！')) return;
    try {
      await API.delete(`/tasks/${id}`);
      alert('删除成功');
      navigate('/');
    } catch (err) {
      alert('删除失败');
    }
  };

  if (loading) return <div style={{textAlign:'center',padding:'60px',color:'#888'}}>加载中...</div>;
  if (!task) return null;

  const getLocation = () => {
    if (task.location_type === '住宅') return `${task.building}${task.unit}${task.floor}层${task.room}`;
    return task.public_area || '';
  };

  const Row = ({ label, value, children }) => (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{children || value || <span style={{color:'#bbb'}}>暂无</span>}</span>
    </div>
  );

  const currentAssignType = editing.assign_type ?? task.assign_type ?? 'department';
  const currentDept = editing.department ?? task.department ?? '';
  const currentAssignUser = editing.assign_user_id ?? task.assign_user_id ?? '';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.back} onClick={() => navigate(-1)}>← 返回</button>
        <h2 style={styles.headerTitle}>事项详情</h2>
        {isAdmin && (
          <button style={styles.deleteBtn} onClick={handleDelete}>🗑️</button>
        )}
      </div>

      <div style={styles.content}>
        {/* 基本信息 */}
        <div style={styles.card}>
          <div style={styles.badges}>
            <span style={{...styles.badge, background: PRIORITY_COLORS[task.priority]}}>{task.priority}</span>
            <span style={{...styles.badge, background: STATUS_COLORS[task.status]}}>{task.status}</span>
            {task.department && <span style={{...styles.badge, background:'#888'}}>{task.department}</span>}
            {task.assign_user_name && <span style={{...styles.badge, background:'#7b2d8b'}}>👤{task.assign_user_name}</span>}
          </div>
          <h3 style={styles.taskTitle}>{task.title}</h3>
          <Row label="来源" value={task.source} />
          <Row label="地点" value={getLocation()} />
          <Row label="录入人" value={task.creator_name} />
          <Row label="录入时间" value={new Date(task.created_at).toLocaleString('zh-CN')} />

          {/* 完成按钮 */}
          {task.status !== '已完成' && (
            <button style={styles.completeBtn} onClick={() => setShowCompleteModal(true)}>
              ✓ 标记已完成（需拍照）
            </button>
          )}
        </div>

        {/* 图片 */}
        {task.images?.length > 0 && (
          <div style={styles.card}>
            <h4 style={styles.sectionTitle}>📷 现场图片</h4>
            <div style={styles.imgGrid}>
              {task.images.map(img => (
                <img key={img.id} src={`/uploads/${img.image_path}`}
                  style={styles.img} alt=""
                  onClick={() => window.open(`/uploads/${img.image_path}`)} />
              ))}
            </div>
          </div>
        )}

        {/* 经理操作 */}
        <div style={styles.card}>
          <h4 style={styles.sectionTitle}>👔 经理审核</h4>
          {isManager ? (
            <>
              <div style={styles.priorityRow}>
                <span style={styles.sectionSubTitle}>🔄 任务调派</span>
                <div style={styles.radioGroup}>
                  {[['department','分派到部门'],['user','分派到个人']].map(([v,l]) => (
                    <button key={v}
                      style={{...styles.radioBtn, ...(currentAssignType===v ? styles.radioBtnActive : {})}}
                      onClick={() => setEditing({...editing, assign_type:v, department:'', assign_user_id:''})}>
                      {l}
                    </button>
                  ))}
                </div>
                {currentAssignType === 'department' && (
                  <div style={{...styles.radioGroup, marginTop:'8px'}}>
                    {['秩序部','工程部','保洁部','管家部','绿化部'].map(d => (
                      <button key={d}
                        style={{...styles.radioBtn, ...(currentDept===d ? styles.radioBtnActive : {})}}
                        onClick={() => setEditing({...editing, department:d, assign_user_id:''})}>
                        {d}
                      </button>
                    ))}
                  </div>
                )}
                {currentAssignType === 'user' && (
                  <select style={{...styles.input, marginTop:'8px'}}
                    value={currentAssignUser}
                    onChange={e => setEditing({...editing, assign_user_id: e.target.value, department:''})}>
                    <option value="">请选择负责人</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.real_name}（{ROLES[u.role]}）</option>)}
                  </select>
                )}
              </div>
              <div style={styles.priorityRow}>
                <span style={styles.sectionSubTitle}>调整优先级</span>
                <div style={styles.radioGroup}>
                  {['紧急','高','中','低'].map(p => (
                    <button key={p}
                      style={{...styles.radioBtn, ...((editing.priority||task.priority)===p ? {background:PRIORITY_COLORS[p],color:'#fff',borderColor:PRIORITY_COLORS[p]} : {})}}
                      onClick={() => setEditing({...editing, priority: p})}>{p}</button>
                  ))}
                </div>
              </div>
              <div style={styles.priorityRow}>
                <span style={styles.sectionSubTitle}>修改状态</span>
                <div style={styles.radioGroup}>
                  {['待处理','处理中','已完成'].map(s => (
                    <button key={s}
                      style={{...styles.radioBtn, ...((editing.status||task.status)===s ? {background:STATUS_COLORS[s],color:'#fff',borderColor:STATUS_COLORS[s]} : {})}}
                      onClick={() => setEditing({...editing, status: s})}>{s}</button>
                  ))}
                </div>
              </div>
              <CnTextarea style={styles.textarea} placeholder="经理意见..."
                value={editing.manager_opinion !== undefined ? editing.manager_opinion : (task.manager_opinion || '')}
                onChange={v => setEditing({...editing, manager_opinion: v})} rows={2} />
              <button style={styles.saveBtn} disabled={saving}
                onClick={() => {
                  const newStatus = editing.status ?? task.status;
                  if (newStatus === '已完成' && task.status !== '已完成') {
                    setShowCompleteModal(true);
                  } else {
                    handleSave({
                      assign_type: currentAssignType, department: currentDept,
                      assign_user_id: currentAssignUser,
                      priority: editing.priority ?? task.priority,
                      status: newStatus,
                      manager_opinion: editing.manager_opinion ?? task.manager_opinion
                    });
                  }
                }}>
                {saving ? '保存中...' : '保存'}
              </button>
            </>
          ) : (
            <>
              <Row label="经理意见" value={task.manager_opinion} />
              <Row label="责任分配" value={task.department || (task.assign_user_name ? `👤${task.assign_user_name}` : '未分配')} />
            </>
          )}
        </div>

        {/* 工程填写 */}
        <div style={styles.card}>
          <h4 style={styles.sectionTitle}>🔧 处理方案</h4>
          {isEngineer ? (
            <>
              <CnTextarea style={styles.textarea} placeholder="解决方案..."
                value={editing.solution !== undefined ? editing.solution : (task.solution || '')}
                onChange={v => setEditing({...editing, solution: v})} rows={3} />
              <CnInput style={styles.input} placeholder="预计处理时间"
                value={editing.expected_time !== undefined ? editing.expected_time : (task.expected_time || '')}
                onChange={v => setEditing({...editing, expected_time: v})} />
              <button style={styles.saveBtn} disabled={saving}
                onClick={() => handleSave({ solution: editing.solution ?? task.solution, expected_time: editing.expected_time ?? task.expected_time })}>
                {saving ? '保存中...' : '保存'}
              </button>
            </>
          ) : (
            <>
              <Row label="解决方案" value={task.solution} />
              <Row label="预计完成" value={task.expected_time} />
            </>
          )}
        </div>

        {/* 业委意见 */}
        <div style={styles.card}>
          <h4 style={styles.sectionTitle}>🏛️ 业主委意见</h4>
          {isCommittee ? (
            <>
              <CnTextarea style={styles.textarea} placeholder="业主委意见..."
                value={editing.committee_opinion !== undefined ? editing.committee_opinion : (task.committee_opinion || '')}
                onChange={v => setEditing({...editing, committee_opinion: v})} rows={2} />
              <button style={styles.saveBtn} disabled={saving}
                onClick={() => handleSave({ committee_opinion: editing.committee_opinion ?? task.committee_opinion })}>
                {saving ? '保存中...' : '保存'}
              </button>
            </>
          ) : (
            <Row label="业委意见" value={task.committee_opinion} />
          )}
        </div>

        {/* 操作日志 */}
        {task.logs?.length > 0 && (
          <div style={styles.card}>
            <h4 style={styles.sectionTitle}>📋 操作记录</h4>
            {task.logs.map(log => (
              <div key={log.id} style={styles.logItem}>
                <span style={styles.logName}>{log.real_name}</span>
                <span style={styles.logAction}>{log.action}</span>
                <span style={styles.logTime}>{new Date(log.created_at).toLocaleString('zh-CN')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 完成拍照弹窗 */}
      {showCompleteModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>✓ 确认完成</h3>
            <div style={styles.photoTip}>📷 请拍照上传完成凭证</div>
            <label style={styles.uploadArea}>
              {completePreview
                ? <img src={completePreview} style={styles.previewImg} alt="" />
                : <><span style={{fontSize:'36px'}}>📷</span><span style={{color:'#888',fontSize:'13px'}}>点击拍照上传</span></>}
              <input type="file" accept="image/*" style={{display:'none'}}
                onChange={e => {
                  const f = e.target.files[0];
                  if (f) { setCompleteImage(f); setCompletePreview(URL.createObjectURL(f)); }
                }} />
            </label>
            <input style={styles.minput} placeholder="完成备注（可选）"
              value={completeNote} onChange={e => setCompleteNote(e.target.value)} />
            <div style={styles.modalBtns}>
              <button style={styles.cancelBtn} onClick={() => setShowCompleteModal(false)}>取消</button>
              <button style={styles.confirmBtn} onClick={handleComplete} disabled={saving}>
                {saving ? '提交中...' : '确认完成'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f5f7fa', paddingBottom: '30px' },
  header: { background: 'linear-gradient(90deg, #1a4a8a, #2d7dd2)', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  back: { background: 'transparent', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer' },
  headerTitle: { color: '#fff', margin: 0, fontSize: '18px', fontWeight: 700 },
  deleteBtn: { background: 'rgba(255,77,79,0.3)', border: 'none', color: '#fff', borderRadius: '8px', padding: '6px 10px', fontSize: '16px', cursor: 'pointer' },
  content: { padding: '16px' },
  card: { background: '#fff', borderRadius: '12px', padding: '16px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  badges: { display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' },
  badge: { color: '#fff', borderRadius: '4px', padding: '3px 10px', fontSize: '13px', fontWeight: 600 },
  taskTitle: { fontSize: '17px', fontWeight: 700, color: '#222', margin: '0 0 12px' },
  completeBtn: { width: '100%', marginTop: '12px', padding: '12px', background: 'linear-gradient(90deg, #1a6a3a, #2d9d5a)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
  sectionTitle: { fontSize: '15px', fontWeight: 700, color: '#1a4a8a', margin: '0 0 12px' },
  sectionSubTitle: { display: 'block', fontSize: '13px', color: '#666', marginBottom: '6px', fontWeight: 600 },
  row: { display: 'flex', padding: '6px 0', borderBottom: '1px solid #f5f5f5' },
  rowLabel: { color: '#888', fontSize: '14px', minWidth: '80px' },
  rowValue: { color: '#333', fontSize: '14px', flex: 1 },
  textarea: { width: '100%', padding: '10px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' },
  input: { width: '100%', padding: '10px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '8px' },
  saveBtn: { padding: '8px 24px', background: '#1a4a8a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  priorityRow: { marginBottom: '14px', padding: '10px', background: '#f8faff', borderRadius: '8px' },
  radioGroup: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' },
  radioBtn: { padding: '6px 14px', border: '1.5px solid #e0e0e0', borderRadius: '16px', background: '#fff', fontSize: '13px', cursor: 'pointer' },
  radioBtnActive: { background: '#1a4a8a', color: '#fff', borderColor: '#1a4a8a' },
  imgGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  img: { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' },
  logItem: { display: 'flex', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f5f5f5', flexWrap: 'wrap' },
  logName: { color: '#1a4a8a', fontWeight: 600, fontSize: '13px' },
  logAction: { color: '#333', fontSize: '13px', flex: 1 },
  logTime: { color: '#999', fontSize: '12px' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 200 },
  modalContent: { background: '#fff', width: '100%', borderRadius: '16px 16px 0 0', padding: '24px 20px', maxHeight: '85vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: '#1a6a3a' },
  photoTip: { background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#1a6a3a', marginBottom: '10px' },
  uploadArea: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #e0e0e0', borderRadius: '12px', padding: '16px', marginBottom: '10px', cursor: 'pointer', minHeight: '100px' },
  previewImg: { width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' },
  minput: { display: 'block', width: '100%', padding: '11px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', marginBottom: '10px', boxSizing: 'border-box' },
  modalBtns: { display: 'flex', gap: '10px', marginTop: '8px' },
  cancelBtn: { flex: 1, padding: '12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '16px', background: '#fff', cursor: 'pointer' },
  confirmBtn: { flex: 1, padding: '12px', background: '#1a6a3a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }
};
